import { useEffect } from 'react';
import { useBrandStore, brandFromChecklist, type ActiveBrand } from '../store/brandStore';
import { useUIStore } from '../store/uiStore';
import { useChecklistStore } from '../store/checklistStore';

/** The brand currently in effect (co-brand strip + accent), or null. */
export function useActiveBrand(): ActiveBrand | null {
  return useBrandStore((s) => s.activeBrand);
}

/**
 * Resolves and keeps the active brand in step with the insurer-link journey.
 * Mount once, at the app root (alongside useSyncWebThemeVars).
 *
 * Two sources: the link itself, uiStore.pendingProperty.checklistId (or,
 * with no home yet, uiStore.pendingModel from an insurer entry page), set the
 * moment `/?property=<id>&model=<checklistId>` is parsed. Resolving it early
 * means the sidebar strip and the accent are up before the visitor reaches
 * the structure screen. The app-wide brand is deliberately NOT adopted from
 * whatever property is being viewed: a FAIR Plan homeowner opening their own
 * page would otherwise carry FAIR Plan across every screen for the rest of
 * the session. The property page shows the viewed model's brand on its own
 * strip instead (BrandStrip's `brand` prop).
 */
export function useBrandResolver(): void {
  const linkChecklistId = useUIStore((s) => s.pendingProperty?.checklistId);
  const pendingModel = useUIStore((s) => s.pendingModel);
  const pendingChecklistId = linkChecklistId ?? pendingModel ?? undefined;
  const fromPendingModel = !linkChecklistId && Boolean(pendingModel);
  const resolvePreviewChecklist = useChecklistStore((s) => s.resolvePreviewChecklist);
  const setActiveBrand = useBrandStore((s) => s.setActiveBrand);

  useEffect(() => {
    if (!pendingChecklistId) return;
    let cancelled = false;
    resolvePreviewChecklist(pendingChecklistId)
      .then((checklist) => {
        if (cancelled) return;
        // A model that did not resolve falls back to the default homeowner
        // model. For an insurer page that would put the wrong insurer's mark
        // on screen, so adopt the brand only when the id matches.
        if (fromPendingModel && checklist?.id !== pendingChecklistId) return;
        setActiveBrand(brandFromChecklist(checklist));
      })
      .catch(() => {
        // Bad or stale model id — resolvePreviewChecklist already falls back
        // internally; nothing further to do here.
      });
    return () => {
      cancelled = true;
    };
  }, [pendingChecklistId, fromPendingModel, resolvePreviewChecklist, setActiveBrand]);
}
